/**
 * Pure helpers for the Performance Targets month picker, trend bars and
 * Target History section. No React, so they are unit-tested directly.
 */

import type { TargetHistoryRow } from '../../hooks/useBrokerTargetHistory';

const YM = /^\d{4}-(0[1-9]|1[0-2])$/;

/** Current Brisbane civil month as 'YYYY-MM' (UTC+10, no daylight saving). */
export function brisbaneMonthNow(nowMs: number = Date.now()): string {
  return new Date(nowMs).toLocaleDateString('en-CA', {
    timeZone: 'Australia/Brisbane',
    year: 'numeric',
    month: '2-digit',
  });
}

export function addMonths(ym: string, delta: number): string {
  const [y, m] = ym.split('-').map(Number);
  const idx = y * 12 + (m - 1) + delta;
  return `${Math.floor(idx / 12)}-${String((idx % 12) + 1).padStart(2, '0')}`;
}

/** 'YYYY-MM' → 'July 2026'. Empty string for anything malformed. */
export function monthLong(ym: string | null | undefined): string {
  if (!ym || !YM.test(ym)) return '';
  const [y, m] = ym.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleString('en-AU', { month: 'long', year: 'numeric' });
}

/** Australian financial year of a month: '2026-07' → 'FY 26-27'. */
export function financialYearOf(ym: string): string {
  const [y, m] = ym.split('-').map(Number);
  const start = m >= 7 ? y : y - 1;
  return `FY ${String(start % 100).padStart(2, '0')}-${String((start + 1) % 100).padStart(2, '0')}`;
}

export interface MonthOption {
  value: string;
  label: string;
}

/**
 * Month picker options, newest first: every month after the newest data up
 * to now (flagged as having no data yet), then the newest data month and the
 * eleven before it. With no data at all, the last twelve months, all flagged.
 */
export function monthOptions(latestDataMonth: string | null, nowYm: string): MonthOption[] {
  const out: MonthOption[] = [];
  const newestWithData = latestDataMonth && latestDataMonth <= nowYm ? latestDataMonth : null;
  const oldest = addMonths(newestWithData ?? nowYm, -11);
  for (let ym = nowYm; ym >= oldest; ym = addMonths(ym, -1)) {
    if (!newestWithData || ym > newestWithData) {
      out.push({ value: ym, label: `${monthLong(ym)} (no data yet)` });
    } else if (ym === newestWithData) {
      out.push({ value: ym, label: `${monthLong(ym)} (latest data)` });
    } else {
      out.push({ value: ym, label: monthLong(ym) });
    }
  }
  return out;
}

/** Rows that have been judged (or are being judged) against real data. */
export function hasData(r: TargetHistoryRow): boolean {
  return r.status !== 'awaiting_data' && r.valueCents !== null;
}

export interface HistorySummary {
  /** Periods whose result is final: met, or missed with every month in. */
  judged: number;
  met: number;
  /** Mean attainment over judged periods, 1 dp; null when none judged. */
  averagePercent: number | null;
  /** Judged row with the highest attainment. */
  best: TargetHistoryRow | null;
  bonusEarnedCents: number;
}

export function summarize(rows: TargetHistoryRow[]): HistorySummary {
  const judgedRows = rows.filter((r) => r.status === 'met' || r.status === 'missed');
  const met = judgedRows.filter((r) => r.status === 'met').length;
  const pcts = judgedRows.map((r) => r.percentComplete ?? 0);
  const best = judgedRows.reduce<TargetHistoryRow | null>(
    (acc, r) => (acc === null || (r.percentComplete ?? 0) > (acc.percentComplete ?? 0) ? r : acc),
    null,
  );
  return {
    judged: judgedRows.length,
    met,
    averagePercent:
      pcts.length > 0 ? Math.round((pcts.reduce((a, b) => a + b, 0) / pcts.length) * 10) / 10 : null,
    best,
    bonusEarnedCents: judgedRows
      .filter((r) => r.status === 'met')
      .reduce((sum, r) => sum + r.bonusCents, 0),
  };
}

export interface TrendBar {
  periodStart: string;
  periodLabel: string;
  valueCents: number;
  /** Bar height, % of the chart (0-100). */
  heightPct: number;
  met: boolean;
}

/**
 * The last `count` periods with data for one target, oldest first, scaled so
 * the threshold line sits at the top unless a period beat it.
 */
export function trendBars(rows: TargetHistoryRow[], targetId: string, count = 7): {
  bars: TrendBar[];
  thresholdPct: number;
} {
  const own = rows
    .filter((r) => r.targetId === targetId && hasData(r))
    .sort((a, b) => a.periodStart.localeCompare(b.periodStart))
    .slice(-count);
  const threshold = own[0]?.thresholdCents ?? 0;
  const scale = Math.max(threshold, ...own.map((r) => r.valueCents ?? 0), 1);
  return {
    bars: own.map((r) => ({
      periodStart: r.periodStart,
      periodLabel: r.periodLabel,
      valueCents: r.valueCents ?? 0,
      heightPct: Math.max(0, Math.min(100, ((r.valueCents ?? 0) / scale) * 100)),
      met: r.status === 'met',
    })),
    thresholdPct: Math.min(100, (threshold / scale) * 100),
  };
}

/** Short axis label for a period: 'Jul' for a month, 'Q1' for a quarter. */
export function shortPeriodLabel(periodLabel: string): string {
  const quarter = periodLabel.match(/^(Q\d)/);
  if (quarter) return quarter[1];
  return periodLabel.slice(0, 3);
}

/**
 * TRUE when every month of the measured period has commission data, so an
 * unmet target is a final miss rather than still in progress. Monthly: the
 * measured month has data. Quarterly: the quarter's last month has data.
 */
export function periodClosed(
  period: 'monthly' | 'quarterly',
  measuredMonth: string | null,
  latestDataMonth: string | null,
): boolean {
  if (!measuredMonth || !latestDataMonth) return false;
  if (period === 'monthly') return measuredMonth <= latestDataMonth;
  const [y, m] = measuredMonth.split('-').map(Number);
  const quarterStart = `${y}-${String(Math.floor((m - 1) / 3) * 3 + 1).padStart(2, '0')}`;
  return addMonths(quarterStart, 2) <= latestDataMonth;
}
